/**
 * Pure helpers for bug-report metadata assembly + module derivation.
 *
 * These are split out from the React component so they're easy to
 * unit-test without spinning up a renderer (the workspace ships
 * without @testing-library/react — see ask-argus memories test).
 */

import {
  getRecentConsoleErrors,
  getRecentConsoleWarnings,
  type RecordedError,
  type RecordedWarning,
} from '@/lib/console-error-tracker';
import {
  getLastApiCalls,
  getLastFailedApiCall,
  type ApiCallRecord,
  type LastFailedApi,
} from '@/lib/failed-api-tracker';
import { getRecentRoutes } from '@/lib/route-history-tracker';

// ─── Module label derivation ─────────────────────────────────────────────────

/**
 * Map URL path segment → human-readable module label.
 *
 * Mirrors the rail entries in `nav-rail.tsx` so the user sees the
 * same name in the bug-report form that they see in the sidebar.
 * Centralised here (rather than reading nav-rail) because nav-rail
 * imports react-router and we want this file to stay pure.
 */
const MODULE_LABELS: Record<string, string> = {
  'ask-argus': 'Ask Argus',
  approvals: 'Approvals',
  agents: 'Agents',
  studio: 'Studio',
  connections: 'Connections',
  library: 'Library',
  automate: 'Automate',
  'argus-live': 'Argus Live',
  insights: 'Insights',
  trust: 'Trust',
  users: 'Users',
  admin: 'Admin',
  playground: 'Reference',
  sysadmin: 'Sysadmin',
  setup: 'Setup',
  invite: 'Invitation',
  login: 'Login',
  auth: 'Auth',
};

/**
 * Derive a human-readable module label from a pathname.
 *
 * Examples:
 *   '/ask-argus'                -> 'Ask Argus'
 *   '/sysadmin/tenants/abc-1'   -> 'Sysadmin'
 *   '/library/foo/bar'          -> 'Library'
 *   '/'                         -> 'Home'
 *   ''                          -> 'Home'
 */
export function deriveModuleLabel(pathname: string): string {
  const first = (pathname || '').split('/').filter(Boolean)[0];
  if (!first) return 'Home';
  return MODULE_LABELS[first] ?? capitalize(first);
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ─── Public metadata shape (matches backend contract) ───────────────────────

export interface BugReportNetworkState {
  online: boolean;
  effectiveType?: string;
}

export interface BugReportMetadata {
  route: string;
  userAgent: string;
  viewport: { w: number; h: number };
  screen: { w: number; h: number };
  appVersion: string;
  environment: string;
  timestamp: string;
  recentErrors: RecordedError[];
  lastFailedApi: LastFailedApi | null;
  tenantId: string | null;
  userId: string | null;

  // ── bBUG2 enrichments ────────────────────────────────────────────
  /** `document.title` at click time. Gives triagers human context. */
  documentTitle: string;
  /**
   * Build SHA wired in by Vite (`VITE_GIT_SHA`). Tells us exactly which
   * deploy the user was on. `null` when the env var isn't set (e.g. a
   * pre-bBUG2 build).
   */
  buildSha: string | null;
  /** Last 5 distinct routes the user walked through (oldest → newest). */
  recentRoutes: string[];
  /** Last 10 console.warn entries (separate from recentErrors). */
  consoleWarnings: RecordedWarning[];
  /** Last 10 API calls observed by the api-client (success or fail). */
  recentApiCalls: ApiCallRecord[];
  /** navigator.onLine + connection.effectiveType. */
  networkState: BugReportNetworkState;
  /** Best-effort CSS selector path for `document.activeElement` at click time. */
  focusedElementSelector: string | null;
}

export interface CollectMetadataInput {
  pathname: string;
  tenantId: string | null;
  userId: string | null;
  /**
   * Snapshot of the focused element BEFORE the async screenshot capture
   * runs. The button captures this synchronously inside onClick and
   * passes it through here. Optional because some call sites (tests)
   * may omit it.
   */
  focusedElementSelector?: string | null;
  /**
   * Test/electron seam: supply the browser globals explicitly. When
   * omitted, real `window` / `navigator` / `import.meta.env` are
   * read (this is the production path). Vitest defaults to a node
   * environment so the helper has to be testable without a real DOM.
   */
  env?: {
    viewport: { w: number; h: number };
    screen: { w: number; h: number };
    userAgent: string;
    appVersion: string;
    environment: string;
    documentTitle: string;
    buildSha: string | null;
    networkState: BugReportNetworkState;
  };
}

function readDefaultEnv(): NonNullable<CollectMetadataInput['env']> {
  // ── Vite env access — `import.meta.env` is always available in a
  //    Vite-built bundle. We read with optional chaining and default
  //    so the value remains useful in dev / Storybook / tests too.
  const env =
    typeof import.meta !== 'undefined' &&
    (import.meta as unknown as { env?: Record<string, string> }).env
      ? (import.meta as unknown as { env: Record<string, string> }).env
      : {};
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 0;
  const viewportH = typeof window !== 'undefined' ? window.innerHeight : 0;
  const screenW =
    typeof window !== 'undefined' && window.screen ? window.screen.width : 0;
  const screenH =
    typeof window !== 'undefined' && window.screen ? window.screen.height : 0;
  const userAgent =
    typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';

  // navigator.connection is an experimental API not on every browser.
  // Guard for undefined; the typed shape we read is intentionally loose.
  let effectiveType: string | undefined;
  let online = true;
  if (typeof navigator !== 'undefined') {
    online = navigator.onLine !== false; // assume online when unknown
    const conn = (
      navigator as unknown as {
        connection?: { effectiveType?: string };
      }
    ).connection;
    if (conn && typeof conn.effectiveType === 'string') {
      effectiveType = conn.effectiveType;
    }
  }

  return {
    viewport: { w: viewportW, h: viewportH },
    screen: { w: screenW, h: screenH },
    userAgent,
    appVersion: env.VITE_APP_VERSION ?? 'dev',
    environment: env.VITE_ARGUSAI_ENV ?? env.MODE ?? 'dev',
    documentTitle:
      typeof document !== 'undefined' && document.title
        ? document.title
        : '',
    buildSha: env.VITE_GIT_SHA && env.VITE_GIT_SHA !== 'unknown'
      ? env.VITE_GIT_SHA
      : null,
    networkState: { online, effectiveType },
  };
}

/**
 * Collect metadata for a bug-report submission. Reads globals from
 * window / navigator / import.meta.env via `readDefaultEnv()`; tests
 * can override by passing the `env` seam.
 */
export function collectMetadata(input: CollectMetadataInput): BugReportMetadata {
  const env = input.env ?? readDefaultEnv();
  return {
    route: input.pathname,
    userAgent: env.userAgent,
    viewport: env.viewport,
    screen: env.screen,
    appVersion: env.appVersion,
    environment: env.environment,
    timestamp: new Date().toISOString(),
    recentErrors: getRecentConsoleErrors(),
    lastFailedApi: getLastFailedApiCall(),
    tenantId: input.tenantId,
    userId: input.userId,
    documentTitle: env.documentTitle,
    buildSha: env.buildSha,
    recentRoutes: getRecentRoutes(),
    consoleWarnings: getRecentConsoleWarnings(),
    recentApiCalls: getLastApiCalls(),
    networkState: env.networkState,
    focusedElementSelector: input.focusedElementSelector ?? null,
  };
}
